import { matchesScope } from './api';

/**
 * Role templates (UI only): a named bundle of scopes applied to a tenant role in one click. Each pairs the hub scope
 * that shows the pages (`ophub:*`) with the service's own admin scope the pages call with. AUTH stores only the
 * resulting scopes, so a template changed later does not touch roles made from it.
 */
export interface RoleTemplate {
  id: string;
  label: string;
  /** Suggested role name (the Entra app role value). */
  name: string;
  hint: string;
  scopes: string[];
}

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    id: 'operator',
    label: 'Operator (everything)',
    name: 'operator',
    hint: 'The whole hub and every service admin API',
    scopes: [
      'ophub:*',
      'auth:tenants:*',
      'scim:endpoints:*',
      'datacollector:*',
      'nycebuddy:admin',
      'automation-gw:admin',
      'printqueue:admin',
      'nycelogic:auth-usage:read',
    ],
  },
  {
    id: 'tenant-admin',
    label: 'Tenant admin',
    name: 'tenant-admin',
    hint: 'Identity: tenants, users, roles, login apps, SSO and SCIM',
    scopes: [
      'ophub:auth:write',
      'ophub:scim:write',
      'auth:tenants:read',
      'auth:tenants:write',
      'scim:endpoints:read',
      'scim:endpoints:write',
      'nycelogic:auth-usage:read',
    ],
  },
  {
    id: 'tenant-reader',
    label: 'Tenant reader',
    name: 'tenant-reader',
    hint: 'Identity, read only',
    scopes: [
      'ophub:auth:read',
      'ophub:scim:read',
      'auth:tenants:read',
      'scim:endpoints:read',
      'nycelogic:auth-usage:read',
    ],
  },
  {
    id: 'buddy-admin',
    label: 'nyce.buddy admin',
    name: 'buddy-admin',
    hint: 'nyce.buddy administration and statistics',
    scopes: ['ophub:buddy:admin', 'nycebuddy:admin'],
  },
  {
    id: 'printqueue-admin',
    label: 'Print queue admin',
    name: 'printqueue-admin',
    hint: 'Print queue jobs, printers, print servers and tokens',
    scopes: ['ophub:printqueue:write', 'printqueue:admin'],
  },
  {
    id: 'automation-admin',
    label: 'Automation admin',
    name: 'automation-admin',
    hint: 'Automation gateway agents, routes, file watchers and logs',
    scopes: ['ophub:automation:write', 'automation-gw:admin'],
  },
  {
    id: 'statistics',
    label: 'Statistics admin',
    name: 'statistics-admin',
    hint: 'Statistics, collector settings and reports',
    scopes: ['ophub:stats:admin', 'datacollector:read', 'datacollector:admin'],
  },
  {
    id: 'billing',
    label: 'Billing',
    name: 'billing',
    hint: 'Billing reports, schedules and sending',
    scopes: ['ophub:billing:write'],
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    name: 'infrastructure',
    hint: 'Cloud Logging, pods and database tools',
    scopes: [
      'ophub:gcplogs:read',
      'ophub:pods:write',
      'ophub:db:read',
      'ophub:db:backup',
      'ophub:db:copy',
      'ophub:db:advise',
    ],
  },
  {
    id: 'hub-admin',
    label: 'Hub admin',
    name: 'hub-admin',
    hint: 'Hub registry and audit log',
    scopes: ['ophub:admin'],
  },
];

/** True when `scopes` hold (or a wildcard among them covers) every scope of the template. */
export const holdsTemplate = (scopes: string[], t: RoleTemplate) =>
  t.scopes.every((s) => matchesScope(scopes, s));

/** `scopes` plus the template's scopes they do not cover yet. */
export const withTemplate = (scopes: string[], t: RoleTemplate) => [
  ...scopes,
  ...t.scopes.filter((s) => !matchesScope(scopes, s)),
];

/** `scopes` without the template's own scopes, keeping those another held template still needs. */
export function withoutTemplate(scopes: string[], t: RoleTemplate): string[] {
  const kept = ROLE_TEMPLATES.filter((o) => o.id !== t.id && holdsTemplate(scopes, o)).flatMap(
    (o) => o.scopes
  );
  return scopes.filter((s) => !t.scopes.includes(s) || kept.includes(s));
}

/** The template a role was made from: the one whose suggested name it carries. */
export const templateOf = (roleName: string) => ROLE_TEMPLATES.find((t) => t.name === roleName);

/** How a role made from `t` differs from it now: scopes to add and scopes the template no longer has. */
export function templateDiff(scopes: string[], t: RoleTemplate) {
  return {
    missing: t.scopes.filter((s) => !scopes.includes(s)),
    extra: scopes.filter((s) => !t.scopes.includes(s)),
  };
}
